import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ArrowUpIcon from "@src/lib/icons/18px/arrow-up";
import { Button } from "@src/lib/ui/Button";
import ComposerBrandSelector from "@src/features/assistantChat/ComposerBrandSelector";
import ChatAttachment, { ChatAttachmentItem } from "../ChatAttachment";
import { ChatAttachMenu } from "../ChatAttachMenu";
import { cn } from "@src/lib/utils/cn";
import { Tooltip, TooltipTrigger, TooltipContent } from "@src/lib/ui/Tooltip";
import { useAiChatComposerFileUpload } from "@src/features/aiAssist/hooks/useAiChatComposerFileUpload";
import { useAiChatComposerLibraryItemTransform } from "@src/features/aiAssist/hooks/useAiChatComposerLibraryItemTransform";

export interface AttachmentProps {
  enableAttachments?: boolean;
  attachments: ChatAttachmentItem[];
  onAttachmentsChanged?: React.Dispatch<
    React.SetStateAction<ChatAttachmentItem[]>
  >;
}

export interface ChatComposerProps {
  input: string;
  onInputChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit?: (e: React.FormEvent) => void;
  isStreaming?: boolean;
  onStop?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  brandSelectorProps?: React.ComponentProps<typeof ComposerBrandSelector>;
  attachmentProps?: AttachmentProps;
  autoFocus?: boolean;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  input,
  onInputChange,
  onSubmit,
  isStreaming = false,
  onStop,
  placeholder = "Ask me anything...",
  disabled = false,
  className = "",
  brandSelectorProps,
  attachmentProps,
  autoFocus = true,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { triggerFileUpload, HiddenFileInput } = useAiChatComposerFileUpload({
    onAttachmentsChanged: attachmentProps?.onAttachmentsChanged,
    enableDragAndDrop: !!attachmentProps?.enableAttachments,
  });

  const { transformLibraryItem } = useAiChatComposerLibraryItemTransform({
    onAttachmentsChanged: attachmentProps?.onAttachmentsChanged,
  });

  const MAX_TEXTAREA_HEIGHT = 240;

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const isOverflowing = textareaRef.current
    ? textareaRef.current.scrollHeight > MAX_TEXTAREA_HEIGHT
    : false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isStreaming && !disabled) {
      onSubmit?.(e);
    }
  };

  // Determine if any attachments are uploading/transforming or have errors
  const hasUploading = attachmentProps?.attachments?.some(
    (att) => att.uploadingFile?.state === "uploading",
  );
  const hasFileError = attachmentProps?.attachments?.some(
    (att) => att.uploadingFile?.state === "error",
  );
  const hasTransforming = attachmentProps?.attachments?.some(
    (att) => att.transformingLibraryItem?.state === "transforming",
  );
  const hasTransformError = attachmentProps?.attachments?.some(
    (att) => att.transformingLibraryItem?.state === "error",
  );

  const hasError = hasFileError || hasTransformError;
  const hasProcessing = hasUploading || hasTransforming;

  const submitDisabled = !input.trim() || disabled || hasProcessing || hasError;
  const submitTooltip = hasUploading
    ? "file upload in progress"
    : hasTransforming
      ? "processing library items"
      : hasFileError
        ? "file uploads failed"
        : hasTransformError
          ? "library item processing failed"
          : undefined;

  const hasAttachments =
    attachmentProps?.attachments && attachmentProps.attachments.length > 0;

  // Ref to the animated attachments toolbar wrapper to anchor the attach menu
  const attachmentsToolbarRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={cn(
        "relative mx-auto flex w-full max-w-2xl flex-col",
        className,
      )}
    >
      {HiddenFileInput}
      <AnimatePresence>
        {hasAttachments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 70 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="mb-[-12px] rounded-t-xl bg-gray-1 py-[5px] pb-[12px]"
            ref={attachmentsToolbarRef}
          >
            <div className="scrollbar-hide flex gap-1.5 overflow-x-auto overflow-y-visible px-[10px] py-[5px]">
              {attachmentProps.attachments.map((attachment, index) => (
                <ChatAttachment
                  key={index}
                  hoverCardSide="top"
                  {...attachment}
                  onRemove={() => {
                    attachmentProps.onAttachmentsChanged?.([
                      ...attachmentProps.attachments.slice(0, index),
                      ...attachmentProps.attachments.slice(index + 1),
                    ]);
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        className={cn(
          "relative z-10 flex flex-col overflow-hidden rounded-xl border border-gray-2 shadow-dashboard-surface",
          !onSubmit ? "bg-gray-1" : "bg-white",
        )}
      >
        {attachmentProps?.enableAttachments && (
          <div className="p-[8px] pb-0">
            <ChatAttachMenu
              onSelectItem={(item) => {
                // Transform library item for chat
                transformLibraryItem(item.libraryItem);
              }}
              onUploadClick={triggerFileUpload}
              excludeLibraryItemIds={
                attachmentProps.attachments
                  .map(
                    (att) =>
                      att.libraryItemFragment?.id ||
                      att.transformingLibraryItem?.libraryItemId,
                  )
                  .filter(Boolean) as string[]
              }
              disabled={disabled}
              anchorElementRef={attachmentsToolbarRef}
            />
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={onInputChange}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          autoFocus={autoFocus}
          data-testid="chat-composer-input"
          className={cn(
            "w-full min-h-[40px] max-h-[240px] resize-none rounded-none border-none p-2 text-[14px] font-normal leading-[20px] tracking-[-0.006em] outline-none placeholder:text-muted-foreground focus:ring-0 disabled:opacity-50",
            className,
            onSubmit ? "bg-white" : "bg-gray-1",
          )}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !disabled) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        {onSubmit && (
          <div
            className={cn(
              "flex items-end justify-between p-2 transition-all duration-200 ease-in-out",
              isOverflowing && "border-t border-input",
            )}
          >
            <div className="flex flex-1 items-center gap-2">
              {brandSelectorProps ? (
                <ComposerBrandSelector {...brandSelectorProps} />
              ) : null}
            </div>
            <div className="ml-2 flex items-center">
              {isStreaming ? (
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  onClick={onStop}
                  disabled={disabled}
                  className="border border-gray-3 bg-gray-1 text-gray-8 hover:bg-gray-2"
                >
                  <CircleStopIcon />
                </Button>
              ) : (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      size="xs"
                      onClick={handleSubmit}
                      disabled={submitDisabled}
                      className="hover:bg-primary-8 size-[28px] rounded-full border border-primary bg-primary p-0 text-white disabled:border-gray-6 disabled:bg-gray-6"
                    >
                      <ArrowUpIcon className="size-[18px]" />
                    </Button>
                  </TooltipTrigger>
                  {submitTooltip && (
                    <TooltipContent>{submitTooltip}</TooltipContent>
                  )}
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CircleStopIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      width="16"
      height="16"
    >
      <rect width="10" height="10" x="3" y="3" rx="2" />
    </svg>
  );
};

export default ChatComposer;
